import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import AddCardModal from "../../components/AddCardModal";
import { getSubscriptionAccessView } from "../../utils/subscriptionAccessState";
import { getStripePromise } from "../../utils/stripeLoader";
import "../business/BusinessBilling.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;
const stripePromise = getStripePromise(API_BASE_URL);

const FreelancerBilling = () => {
  const [invoices, setInvoices] = useState([]);
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  const fetchBillingData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("ogestyle_token");

      if (!token) {
        navigate("/login");
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      // Fetch invoices, upcoming invoice, payment methods, and subscription status in parallel
      const [invoicesRes, upcomingRes, paymentMethodsRes, statusRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/subscription-new/invoices`, { headers }),
        axios
          .get(`${API_BASE_URL}/api/subscription-new/upcoming-invoice`, {
            headers,
          })
          .catch(() => null),
        axios.get(`${API_BASE_URL}/api/subscription-new/payment-methods`, {
          headers,
        }),
        axios.get(`${API_BASE_URL}/api/subscription-new/status`, { headers }),
      ]);

      setInvoices(invoicesRes.data.invoices || []);
      setUpcomingInvoice(upcomingRes?.data?.upcomingInvoice || null);
      setPaymentMethods(paymentMethodsRes.data.paymentMethods || []);
      setSubscription(statusRes.data || null);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load billing information",
      );
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, navigate]);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  const handleAddCard = () => {
    setShowAddCardModal(true);
  };

  // accessState decides the wording (see utils/subscriptionAccessState.js).
  const accessView = getSubscriptionAccessView(subscription);
  const requiresSubscriptionPayment = accessView.blocking;
  const hasPaymentMethod = paymentMethods.length > 0;

  const handleCardAdded = () => {
    setShowAddCardModal(false);
    if (requiresSubscriptionPayment) {
      navigate("/dashboard/freelancer/subscription/checkout");
      return;
    }
    fetchBillingData();
  };

  const handleRemoveCard = async (paymentMethodId) => {
    if (
      !window.confirm("Are you sure you want to remove this payment method?")
    ) {
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(
        `${API_BASE_URL}/api/subscription-new/payment-methods/${paymentMethodId}`,
        { headers },
      );

      alert("Payment method removed successfully");
      fetchBillingData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to remove payment method");
    } finally {
      setProcessing(false);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      setProcessing(true);
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(
        `${API_BASE_URL}/api/subscription-new/payment-methods/${paymentMethodId}/default`,
        {},
        { headers },
      );

      alert("Default payment method updated successfully");
      fetchBillingData();
    } catch (err) {
      alert(
        err.response?.data?.message ||
          "Failed to update default payment method",
      );
    } finally {
      setProcessing(false);
    }
  };



  const viewInvoiceDetail = async (invoiceId) => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(
        `${API_BASE_URL}/api/subscription-new/invoice/${invoiceId}`,
        { headers },
      );

      setSelectedInvoice(response.data.invoice);
    } catch (err) {
      alert("Failed to load invoice details");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      paid: { label: "Paid", className: "status-paid" },
      open: { label: "Open", className: "status-open" },
      void: { label: "Void", className: "status-void" },
      uncollectible: { label: "Failed", className: "status-failed" },
      draft: { label: "Draft", className: "status-draft" },
    };

    const statusInfo = statusMap[status] || {
      label: status,
      className: "status-default",
    };
    return (
      <span className={`status-badge ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  const handleSubscriptionAction = () => {
    if (!requiresSubscriptionPayment) {
      return;
    }

    if (accessView.action === "reactivate") {
      navigate("/dashboard/freelancer/settings?tab=subscription");
      return;
    }

    if (accessView.action === "add_card" || !hasPaymentMethod) {
      handleAddCard();
      return;
    }

    navigate("/dashboard/freelancer/subscription/checkout");
  };

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div className="billing-container">
          <div className="billing-header">
            <button
              className="btn-back"
              onClick={() =>
                navigate("/dashboard/freelancer/settings?tab=subscription")
              }
            >
              ← Back to Subscription
            </button>
            <h1>📄 Billing & Invoices</h1>
          </div>
          <div className="loading-state">Loading billing information...</div>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  return (
    <FreelancerSidebarLayout>
      <div className="billing-container">
        <div className="billing-header">
          <button
            className="btn-back"
            onClick={() =>
              navigate("/dashboard/freelancer/settings?tab=subscription")
            }
          >
            ← Back to Subscription
          </button>
          <h1>📄 Billing & Invoices</h1>
          <p>
            Access your invoices, payment history, and manage payment methods
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
            padding: "20px 24px",
            marginBottom: "24px",
            borderRadius: "12px",
            background: requiresSubscriptionPayment ? "#fff7ed" : "#f9fafb",
            border: `1px solid ${requiresSubscriptionPayment ? "#fdba74" : "#e5e7eb"}`,
          }}
        >
          <div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#111827",
                marginBottom: "6px",
              }}
            >
              {requiresSubscriptionPayment
                ? accessView.title
                : "No subscription payment due"}
            </div>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>
              {requiresSubscriptionPayment
                ? hasPaymentMethod
                  ? accessView.message
                  : "Add a payment method to continue."
                : "Your billing page is up to date. A checkout action will become available here when payment is required."}
            </div>
          </div>
          <button
            className="btn-primary"
            onClick={handleSubscriptionAction}
            disabled={!requiresSubscriptionPayment}
            style={{
              opacity: requiresSubscriptionPayment ? 1 : 0.6,
              cursor: requiresSubscriptionPayment ? "pointer" : "not-allowed",
              minWidth: "240px",
            }}
          >
            {requiresSubscriptionPayment
              ? hasPaymentMethod || accessView.action === "reactivate"
                ? accessView.ctaLabel
                : "Add payment method to continue"
              : "No subscription payment due"}
          </button>
        </div>

        {/* Upcoming Invoice Section */}
        {upcomingInvoice && (
          <div className="upcoming-invoice-card">
            <h2>📅 Upcoming Invoice</h2>
            <div className="upcoming-content">
              <div className="upcoming-info">
                <div className="info-item">
                  <span className="info-label">Next Billing Date:</span>
                  <span className="info-value">
                    {formatDate(upcomingInvoice.nextPaymentAttempt)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Amount Due:</span>
                  <span className="info-value amount-highlight">
                    {formatCurrency(upcomingInvoice.total)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Billing Period:</span>
                  <span className="info-value">
                    {formatDate(upcomingInvoice.periodStart)} -{" "}
                    {formatDate(upcomingInvoice.periodEnd)}
                  </span>
                </div>
              </div>
              {upcomingInvoice.lineItems &&
                upcomingInvoice.lineItems.length > 0 && (
                  <div className="line-items-preview">
                    <h3>Items:</h3>
                    {upcomingInvoice.lineItems.map((item, idx) => (
                      <div key={idx} className="line-item-row">
                        <span className="item-description">
                          {item.description}
                        </span>
                        <span className="item-amount">
                          {formatCurrency(item.amount)}
                        </span>
                        {item.proration && (
                          <span className="proration-badge">Prorated</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Payment Methods Section */}
        <div className="payment-methods-section">
          <div className="section-header">
            <h2>💳 Payment Methods</h2>
            <button
              className="btn-add-card"
              onClick={handleAddCard}
              disabled={processing}
            >
              + Add New Card
            </button>
          </div>
          {paymentMethods.length > 0 ? (
            <div className="payment-methods-list">
              {paymentMethods.map((method) => (
                <div key={method.id} className="payment-method-card">
                  <div className="card-icon">{method.brand.toUpperCase()}</div>
                  <div className="card-details">
                    <div className="card-number">•••• {method.last4}</div>
                    <div className="card-expiry">
                      Expires {method.expMonth}/{method.expYear}
                    </div>
                  </div>
                  {method.isDefault && (
                    <span className="default-badge">Default</span>
                  )}
                  <div className="card-actions">
                    {!method.isDefault && (
                      <button
                        className="btn-action btn-set-default"
                        onClick={() => handleSetDefault(method.id)}
                        disabled={processing}
                        title="Set as default"
                      >
                        Set Default
                      </button>
                    )}
                    {paymentMethods.length > 1 && (
                      <button
                        className="btn-action btn-remove"
                        onClick={() => handleRemoveCard(method.id)}
                        disabled={processing}
                        title="Remove card"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No payment methods on file</p>
              <button className="btn-primary" onClick={handleAddCard}>
                Add Your First Card
              </button>
            </div>
          )}
        </div>

        {/* Invoice History Section */}
        <div className="invoice-history-section">
          <h2>📊 Invoice History</h2>
          {invoices.length > 0 ? (
            <div className="invoice-table-container">
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invoice #</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>{formatDate(invoice.created)}</td>
                      <td className="invoice-number">
                        {invoice.number || invoice.id.substring(3, 13)}
                      </td>
                      <td>{invoice.description}</td>
                      <td className="amount-cell">
                        {formatCurrency(invoice.amount)}
                      </td>
                      <td>{getStatusBadge(invoice.status)}</td>
                      <td className="actions-cell">
                        <button
                          className="btn-view"
                          onClick={() => viewInvoiceDetail(invoice.id)}
                          title="View Details"
                        >
                          View
                        </button>
                        <a
                          href={`${API_BASE_URL}/api/subscription-new/invoice/${invoice.id}/pdf?token=${localStorage.getItem("ogestyle_token")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-download"
                          title="Download OGESTYLE PDF"
                        >
                          PDF
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No invoices yet</p>
              <small>
                Your invoice history will appear here once you receive your
                first invoice.
              </small>
            </div>
          )}
        </div>

        {/* Invoice Detail Modal */}
        {selectedInvoice && (
          <div
            className="modal-overlay"
            onClick={() => setSelectedInvoice(null)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Invoice Details</h2>
                <button
                  className="btn-close"
                  onClick={() => setSelectedInvoice(null)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="invoice-info-grid">
                  <div className="info-item">
                    <span className="info-label">Invoice Number:</span>
                    <span className="info-value">
                      {selectedInvoice.number || "N/A"}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Date:</span>
                    <span className="info-value">
                      {formatDate(selectedInvoice.created)}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Status:</span>
                    <span className="info-value">
                      {getStatusBadge(selectedInvoice.status)}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Period:</span>
                    <span className="info-value">
                      {formatDate(selectedInvoice.periodStart)} -{" "}
                      {formatDate(selectedInvoice.periodEnd)}
                    </span>
                  </div>
                </div>

                <h3>Line Items</h3>
                <div className="line-items-detail">
                  {selectedInvoice.lineItems.map((item, idx) => (
                    <div key={idx} className="line-item-detail-row">
                      <div className="item-info">
                        <div className="item-description">
                          {item.description}
                        </div>
                        {item.periodStart && (
                          <div className="item-period">
                            {formatDate(item.periodStart)} -{" "}
                            {formatDate(item.periodEnd)}
                          </div>
                        )}
                        {item.quantity > 1 && (
                          <div className="item-quantity">
                            Qty: {item.quantity}
                          </div>
                        )}
                      </div>
                      <div className="item-amount-wrapper">
                        {formatCurrency(item.amount)}
                        {item.proration && (
                          <span className="proration-badge">Prorated</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="invoice-totals">
                  <div className="total-row">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  <div className="total-row total-final">
                    <span>Total:</span>
                    <span>{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                  <div className="total-row">
                    <span>Amount Paid:</span>
                    <span className="paid-amount">
                      {formatCurrency(selectedInvoice.amountPaid)}
                    </span>
                  </div>
                </div>

                <div className="modal-actions">
                  <a
                    href={`${API_BASE_URL}/api/subscription-new/invoice/${selectedInvoice.id}/pdf?token=${localStorage.getItem("ogestyle_token")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                  >
                    Download OGESTYLE PDF
                  </a>
                  {selectedInvoice.hostedUrl && (
                    <a
                      href={selectedInvoice.hostedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary"
                    >
                      View in Stripe
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Card Modal */}
        {showAddCardModal && (
          <Elements stripe={stripePromise}>
            <AddCardModal
              onClose={() => setShowAddCardModal(false)}
              onSuccess={handleCardAdded}
              apiBaseUrl={API_BASE_URL}
            />
          </Elements>
        )}
      </div>
    </FreelancerSidebarLayout>
  );
};

export default FreelancerBilling;
